import { useEffect, useMemo, useState } from 'react';
import { History, Search, ArrowUpRight, MinusCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PageLoader } from '../components/PageLoader';
import { InlineError } from '../components/InlineError';
import { formatDateTime } from '../lib/text';
import { labelText, sourceHref } from '../lib/knowledge';
import type { TimelineEvent } from '../lib/types';
import SourceLink from '../components/SourceLink';

function byDay(events: TimelineEvent[]): [string, TimelineEvent[]][] {
    const groups: Record<string, TimelineEvent[]> = {};
    const order: string[] = [];
    for (const ev of events) {
        const day = ev.at.slice(0, 10);
        if (!(day in groups)) { groups[day] = []; order.push(day); }
        groups[day].push(ev);
    }
    return order.map(d => [d, groups[d]]);
}

const dayLabel = (day: string) =>
    new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric',
    });

export default function TimelinePage() {
    const { authFetch } = useAuth();
    const [events, setEvents] = useState<TimelineEvent[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [kind, setKind] = useState<'all' | 'fact' | 'relationship'>('all');
    const [query, setQuery] = useState<string>('');

    useEffect(() => {
        authFetch('/knowledge/timeline')
            .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed to load timeline')))
            .then((data: TimelineEvent[]) => setEvents(data))
            .catch(err => { setError(err.message); setEvents([]); });
    }, [authFetch]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (events ?? []).filter(e =>
            (kind === 'all' || e.kind === kind) &&
            (!q || e.title.toLowerCase().includes(q) || e.type.toLowerCase().includes(q))
        );
    }, [events, kind, query]);

    const grouped = useMemo(() => byDay(filtered), [filtered]);

    if (events === null) return <PageLoader label="Loading timeline..." />;

    return (
        <div>
            <div className="pb-6 mb-6 border-b border-softgrey">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                    <History className="w-5 h-5 text-brand" /> Timeline
                </h3>
                <p className="text-sm text-coolslate mt-1">
                    A business-time record of everything in your company's knowledge — when each
                    customer, invoice, relationship and filing began, and when any ended. Dated by
                    the underlying data, not when it was synced.
                </p>
            </div>

            {error && <InlineError message={error} />}

            <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="flex flex-wrap gap-1.5">
                    {([['all', 'All'], ['fact', 'Records'], ['relationship', 'Relationships']] as const).map(([k, label]) => (
                        <button
                            key={k}
                            onClick={() => setKind(k)}
                            className={`px-3 py-1.5 text-[12px] font-medium rounded-full border transition-colors ${
                                kind === k
                                    ? 'bg-brand text-white border-brand'
                                    : 'bg-white text-coolslate border-softgrey hover:border-brand/40'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                <div className="relative ml-auto">
                    <Search className="w-3.5 h-3.5 text-coolslate absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Filter events…"
                        className="pl-8 pr-3 py-1.5 text-[13px] border border-softgrey rounded-lg bg-white text-graphite w-56 focus:outline-none focus:border-brand/40"
                    />
                </div>
            </div>

            {filtered.length === 0 ? (
                <p className="text-sm text-coolslate py-16 text-center">
                    {(events.length === 0)
                        ? 'No dated events yet. Connect a source to populate your timeline.'
                        : 'No events match this filter.'}
                </p>
            ) : (
                <div className="space-y-8">
                    {grouped.map(([day, dayEvents]) => (
                        <div key={day}>
                            <h4 className="text-[13px] font-semibold text-graphite mb-3 sticky top-0 bg-white/80 backdrop-blur-sm py-1">
                                {dayLabel(day)}
                            </h4>
                            <ol className="relative border-l border-softgrey ml-2 space-y-3">
                                {dayEvents.map(ev => (
                                    <li key={ev.id} className="ml-5">
                                        <span
                                            className={`absolute -left-[7px] w-3.5 h-3.5 rounded-full border-2 border-white ${
                                                ev.event === 'ended' ? 'bg-state-error/70' : 'bg-brand'
                                            }`}
                                        />
                                        <div className="flex items-start justify-between gap-3 px-3.5 py-2.5 border border-softgrey rounded-lg bg-white">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    {ev.event === 'ended' && (
                                                        <MinusCircle className="w-3.5 h-3.5 text-state-error shrink-0" />
                                                    )}
                                                    <span className="text-sm font-medium text-graphite">{ev.title}</span>
                                                    <span className="text-[10px] font-medium text-coolslate bg-offwhite border border-softgrey rounded px-1.5 py-0.5">
                                                        {labelText(ev.type)}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2 mt-1 text-[12px] text-coolslate flex-wrap">
                                                    <span>{formatDateTime(ev.at, false)}</span>
                                                    {ev.detail && <span>· {ev.detail}</span>}
                                                    {ev.source && (
                                                        <>
                                                            <span>·</span>
                                                            <SourceLink
                                                                source={ev.source}
                                                                className="inline-flex items-center gap-0.5 text-brand hover:underline"
                                                            >
                                                                {ev.source.name ?? ev.source.kind}
                                                                {sourceHref(ev.source) && <ArrowUpRight className="w-3 h-3" />}
                                                            </SourceLink>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
